/**
 * useVirtualServers - Hook for fetching virtual servers from Membership API
 * 
 * Virtual servers are curated tool bundles designed for specific agent use cases.
 * This hook fetches and caches the list of virtual servers for a parent MCP service.
 * 
 * @example
 * ```tsx
 * const { virtualServers, loading, error, refresh } = useVirtualServers(serviceId);
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import type { VirtualServer, VirtualServersResponse } from '../types';

interface UseVirtualServersResult {
  virtualServers: VirtualServer[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

// In-memory cache with 5-minute TTL
const cache = new Map<string, { data: VirtualServer[]; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000;

export function useVirtualServers(serviceId: string | null): UseVirtualServersResult {
  const [virtualServers, setVirtualServers] = useState<VirtualServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    // Clear cache for this service
    if (serviceId) {
      cache.delete(serviceId);
    }
    setRefreshKey(prev => prev + 1);
  }, [serviceId]);

  useEffect(() => {
    if (!serviceId) {
      setVirtualServers([]);
      setLoading(false);
      return;
    }

    let mounted = true;

    const fetchVirtualServers = async () => {
      // Check cache first
      const cached = cache.get(serviceId);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        setVirtualServers(cached.data);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/v1/mcp/virtual-servers?parent_server_id=${encodeURIComponent(serviceId)}`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch virtual servers: ${response.statusText}`);
        }

        const data = await response.json();
        // API returns array directly OR wrapped in {virtual_servers: [...]}
        const servers: VirtualServer[] = Array.isArray(data) ? data : (data.virtual_servers || []);

        // Update cache
        cache.set(serviceId, { data: servers, timestamp: Date.now() });

        if (mounted) {
          setVirtualServers(servers);
          setLoading(false);
        }
      } catch (err) {
        console.error('[useVirtualServers] Error:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };

    fetchVirtualServers();

    return () => {
      mounted = false;
    };
  }, [serviceId, refreshKey]);

  return { virtualServers, loading, error, refresh };
}

/**
 * Clear the virtual servers cache (useful when subaccount switches)
 */
export function clearVirtualServersCache(): void {
  cache.clear();
}

/**
 * Invalidate cache entries matching a pattern
 */
export function invalidateVirtualServersCache(pattern: string): void {
  for (const key of cache.keys()) {
    if (key.includes(pattern)) {
      cache.delete(key);
    }
  }
}

